import { Lock } from "Shared/appIcons";
import type { NavItem } from "Shared/Layouts/components/NavButton";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Props = {
	item: NavItem;
	active: boolean;
};

/**
 * Pill-style nav button used in the horizontal topbar.
 * (The vertical drawer variant lives in `NavButton.tsx`.)
 */
export const HorizontalNavButton = ({ item, active }: Props) => {
	const Icon = item.icon;
	const disabled = !!item.disabled;
	const showHint = disabled && !!item.hint;

	const className = cn(
		"inline-flex items-center gap-2 px-3 h-9 rounded-md text-small font-medium transition-colors whitespace-nowrap no-underline",
		disabled
			? "text-muted-foreground cursor-not-allowed"
			: active
				? "bg-primary-light text-primary"
				: "text-foreground hover:bg-muted hover:text-foreground",
	);

	const iconClass = cn(
		"w-4 h-4 flex-shrink-0",
		disabled
			? "text-muted-foreground"
			: active
				? "text-primary"
				: "text-muted-foreground",
	);

	const inner = (
		<>
			<Icon className={iconClass} strokeWidth={1.75} />
			<span>{item.label}</span>
			{showHint ? (
				<span className="text-muted-foreground ml-1 hidden items-center gap-1 text-[10px] uppercase tracking-wide lg:inline-flex">
					<Lock className="h-2.5 w-2.5" />
					{item.hint}
				</span>
			) : null}
		</>
	);

	if (disabled || !item.to) {
		return (
			<Button
				variant="ghost"
				disabled
				title={showHint ? `${item.label} · ${item.hint}` : item.label}
				className={className}
			>
				{inner}
			</Button>
		);
	}

	return (
		<Link
			to={item.to}
			title={item.label}
			className={className}
			onMouseEnter={item.preload}
			onFocus={item.preload}
		>
			{inner}
		</Link>
	);
};
